import React from "react";
import { CircularProgress, DialogContentText, Typography, DialogTitle, Stack, IconButton } from "@mui/material";
import ModalLayout from "./Layout";
import ModalLayoutContent from "./Layout/Content";
import ModalLayoutFooter from "./Layout/Footer";
import ModalLayoutBtn from "./Layout/Btn";
import { X, StopCircle } from "react-feather";

/**
 * Modal for confirming sandbox stop
 *
 * @param {{
 *  isShown: boolean;
 *  onHide: () => void;
 *  onConfirm: () => void;
 *  isStopping: boolean;
 *  sandboxName: string;
 * }} data
 */
const StopSandboxModal = ({
  isShown,
  onHide,
  onConfirm,
  isStopping,
  sandboxName,
}) => {
  return (
    <ModalLayout
      isShown={isShown}
      onHide={onHide}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          overflow: "hidden",
          background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
          border: "1px solid rgba(0,0,0,0.08)",
          transition: "all 0.3s ease-in-out"
        }
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        position={"relative"}
        padding={"16px 24px"}
        borderBottom={"1px solid rgba(0,0,0,0.05)"}
        sx={{ background: "rgba(255,235,205,0.6)" }}
      >
        <DialogTitle
          padding={"0 !important"}
          sx={{
            color: "#ff9800",
            fontWeight: 800,
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            "::before": {
              content: '""',
              display: "inline-block",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#ff9800",
              marginRight: "10px",
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(255, 152, 0, 0.7)" },
                "70%": { boxShadow: "0 0 0 10px rgba(255, 152, 0, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(255, 152, 0, 0)" }
              }
            }
          }}
        >
          Caution!
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onHide}
          sx={{
            position: "absolute",
            right: 16,
            top: 10,
            color: "#495057",
            "&:hover": {
              background: "rgba(0,0,0,0.05)",
              transform: "rotate(90deg)",
              transition: "transform 0.3s ease"
            }
          }}
        >
          <X size={16} />
        </IconButton>
      </Stack>
      <ModalLayoutContent sx={{ padding: "24px 28px" }}>
        <DialogContentText
          textAlign={"center"}
          color="#4a4a4a"
          sx={{ fontSize: "11px", lineHeight: 1.6 }}
        >
          Stop{" "}
          <Typography
            component={"span"}
            fontWeight={700}
            sx={{
              fontSize: "11px",
              color: "#1d3557",
              background: "rgba(241,250,255,0.8)",
              padding: "2px 6px",
              borderRadius: "4px",
              border: "1px solid rgba(0,123,255,0.2)"
            }}
          >
            {sandboxName || "this sandbox"}
          </Typography>
          ?
          <br />
          <Typography
            component={"span"}
            sx={{
              fontSize: "11px",
              color: "#856404",
              background: "rgba(255,243,205,0.8)",
              display: "block",
              margin: "16px auto 0",
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px dashed rgba(255,152,0,0.3)",
              maxWidth: "90%",
              animation: "fadeIn 1.1s ease-in",
              "@keyframes fadeIn": {
                "0%": { opacity: 0, transform: "translateY(-5px)" },
                "50%": { opacity: 0.5, transform: "translateY(-5px)" },
                "100%": { opacity: 1, transform: "translateY(0)" }
              }
            }}
          >
            The environment will be paused. You can resume it later from this page.
          </Typography>
        </DialogContentText>
      </ModalLayoutContent>
      <ModalLayoutFooter sx={{ padding: "16px 24px", gap: "12px" }}>
        <ModalLayoutBtn
          color="default"
          onClick={onHide}
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s",
            padding: "8px 16px",
            color: "#5a6268",
            background: "linear-gradient(45deg, #f5f5f5, #e9ecef)",
            border: "1px solid #dee2e6",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              background: "linear-gradient(45deg, #e9ecef, #dee2e6)",
            }
          }}
        >
          <X size={16} style={{ marginRight: "6px" }} /> Cancel
        </ModalLayoutBtn>
        <ModalLayoutBtn
          disabled={isStopping}
          onClick={onConfirm}
          variant="contained"
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            transition: "all 0.2s",
            background: "linear-gradient(45deg, #ff9800, #ffa726)",
            color: "#ffffff",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(255,152,0,0.25)"
            },
            "&:disabled": {
              background: "#b0bec5",
              color: "#ffffff"
            }
          }}
        >
          <StopCircle size={16} style={{ marginRight: "6px" }} />{" "}
          {isStopping ? (
            <>
              Stopping...&nbsp;&nbsp;
              <CircularProgress size={16} color="inherit" />
            </>
          ) : (
            "Stop"
          )}
        </ModalLayoutBtn>
      </ModalLayoutFooter>
    </ModalLayout>
  );
};

export default StopSandboxModal;
